import { animated, useSpring } from "@react-spring/web";
import { curveBasis, curveLinear, curveNatural, curveStep } from "@visx/curve";
import { LinearGradient } from "@visx/gradient";
import { Group } from "@visx/group";
import { AreaClosed, LinePath } from "@visx/shape";
import React, { useState } from "react";
import { DataPoint } from "../../types";
import { LineProps } from "./types";

const curveMapping = {
  basis: curveBasis,
  linear: curveLinear,
  step: curveStep,
  natural: curveNatural,
};

const Line = ({
  top,
  left,
  data,
  getSidePoint,
  getBottomPoint,
  yMax = 0,
  bottomScale,
  colour = "#2DBFBE",
  curve = "linear",
  areaColour,
  sideScale,
}: LineProps) => {
  const [lineLength, setLineLength] = useState<number>(0);

  const spring = useSpring({
    to: { strokeDasharray: lineLength, strokeDashoffset: 0 },
    from: { strokeDasharray: lineLength, strokeDashoffset: lineLength },
    config: { duration: 500 },
    reset: bottomScale,
  });

  const getXPoint = (point: DataPoint) => {
    const x = getBottomPoint?.(point);
    return bottomScale.bandwidth ? x + bottomScale.bandwidth?.() / 2 : x;
  };

  const getYPoint = (point: DataPoint) => {
    const y = getSidePoint?.(point);

    const nodeHeight = yMax - y;

    return yMax - nodeHeight;
  };

  return (
    <Group
      top={top}
      left={left}
    >
      {areaColour ? (
        <>
          <LinearGradient
            id="area-gradient"
            from={areaColour}
            to={areaColour}
            toOpacity={0.1}
          />

          <AreaClosed
            data={data}
            x={getXPoint}
            y={getYPoint}
            yScale={sideScale}
            strokeWidth={1}
            stroke="url(#area-gradient)"
            fill="url(#area-gradient)"
            curve={curveMapping[curve]}
          />
        </>
      ) : null}

      <LinePath
        curve={curveMapping[curve]}
        x={getXPoint}
        y={getYPoint}
      >
        {({ path }) => {
          const lineData = path(data) ?? "";

          return (
            <>
              <animated.path
                d={lineData}
                style={spring}
                ref={(ref) => {
                  if (ref) {
                    setLineLength(ref.getTotalLength());
                  }
                }}
                stroke={colour}
                strokeWidth={2}
                strokeOpacity={1}
                fill="none"
              />
            </>
          );
        }}
      </LinePath>

      {data.length === 1 ? (
        <circle
          r={4}
          cx={getXPoint(data[0])}
          cy={getYPoint(data[0])}
          fill={colour}
        />
      ) : null}
    </Group>
  );
};

export default Line;
